// game/constants.js
// Core game constants

export const GAME_CONFIG = {
    TICK_RATE: 100, // Game tick every 100ms for smooth progress bars
    SAVE_INTERVAL: 30000, // Auto-save every 30 seconds
    MAX_LEVEL: 99,
    VIRTUAL_LEVEL_CAP: 120,
    BASE_XP: 0,
};

// Bank configuration
export const BANK_CONFIG = {
    BASE_SLOTS: 200,  // Base slots (upgradeable via Architecting milestones)
    MAX_SLOTS: 500,   // Maximum with all upgrades (200 + 50 + 100 + 150)
    MAX_STACK: Number.MAX_SAFE_INTEGER, // 9007199254740991
};

// Offline progress configuration
export const OFFLINE_CONFIG = {
    MAX_TIME_MS: 24 * 60 * 60 * 1000, // 24 hours max
    MIN_TIME_MS: 60 * 1000, // 1 minute minimum to show modal
    EFFICIENCY: 1.0, // 100% efficiency
};

// Farm plot configuration
export const FARM_CONFIG = {
    STARTING_PLOTS: 4,
    MAX_PLOTS: 12,
    UNLOCK_LEVELS: {
        0: 1,   // Plot 1 - Start with 4 plots
        1: 1,   // Plot 2
        2: 1,   // Plot 3
        3: 1,   // Plot 4
        4: 20,  // Plot 5 - Level 20
        5: 35,  // Plot 6 - Level 35
        6: 50,  // Plot 7 - Level 50
        7: 60,  // Plot 8 - Level 60
        8: 70,  // Plot 9 - Level 70
        9: 80,  // Plot 10 - Level 80
        10: 90, // Plot 11 - Level 90
        11: 99, // Plot 12 - Level 99
    },
    AUTO_HARVEST_UNLOCK_LEVEL: 50,
};

// BloomHold configuration (seed vault)
export const BLOOMHOLD_CONFIG = {
    BASE_SLOTS: 50,           // Base slots (upgradeable via Architecting milestones)
    MAX_SLOTS: 100,           // Maximum with all upgrades (50 + 50)
    MAX_STACK: Number.MAX_SAFE_INTEGER,
    UNLOCK_LEVEL: 45,         // Farming level requirement
    UNLOCK_PRICE: 25000,      // Gold cost to purchase
    ACCEPTED_CATEGORIES: [
        'seed',           // Crop seeds
        'tree_seed',      // Tree seeds
        'herb_seed',      // Herb seeds
        'crop',           // Harvested crops
        'herb_grimy',     // Grimy herbs
        'herb_clean',     // Clean herbs
        'vial'            // Vials (for potion brewing)
    ]
};

// XP table - Classic MMORPG formula
// Extended to 120 for virtual levels support
export const XP_TABLE = (() => {
    const table = [0];
    let total = 0;
    for (let level = 1; level < GAME_CONFIG.VIRTUAL_LEVEL_CAP; level++) {
        total += Math.floor(level + 300 * Math.pow(2, level / 7));
        table.push(Math.floor(total / 4));
    }
    return table;
})();

// Skill IDs
export const SKILLS = {
    MINING: 'mining',
    WOODCUTTING: 'woodcutting',
    FISHING: 'fishing',
    PILFERING: 'pilfering',
    FARMING: 'farming',
    ARCHITECTING: 'architecting',
    CRAFTING: 'crafting',
    SMITHING: 'smithing',
    PYRE: 'pyre',
    COOKING: 'cooking',
    WOODWORKING: 'woodworking',
    GLYPHBINDING: 'glyphbinding',
    ALCHEMY: 'alchemy',
    COMBAT: 'combat',
    GATHERER: 'gatherer',
    HUSBANDRY: 'husbandry',
    BOUNTY: 'bounty',
};

// Skill categories for locked insights and quest rewards
export const SKILL_CATEGORIES = {
    COMBAT: {
        id: 'combat',
        name: 'Combat',
        icon: '⚔️',
        skills: [SKILLS.COMBAT]
    },
    GATHERING: {
        id: 'gathering',
        name: 'Gathering',
        icon: '⛏️',
        skills: [SKILLS.MINING, SKILLS.WOODCUTTING, SKILLS.FISHING, SKILLS.GATHERER, SKILLS.FARMING]
    },
    PRODUCTION: {
        id: 'production',
        name: 'Production',
        icon: '🔨',
        skills: [SKILLS.SMITHING, SKILLS.COOKING, SKILLS.CRAFTING, SKILLS.WOODWORKING, SKILLS.GLYPHBINDING, SKILLS.ALCHEMY]
    },
    SUPPORT: {
        id: 'support',
        name: 'Support',
        icon: '✨',
        skills: [SKILLS.PYRE, SKILLS.ARCHITECTING, SKILLS.HUSBANDRY, SKILLS.PILFERING, SKILLS.BOUNTY]
    }
};

/**
 * Get the category a skill belongs to
 * @param {string} skillId - The skill ID to look up
 * @returns {Object|null} The category object or null if not found
 */
export function getSkillCategory(skillId) {
    for (const category of Object.values(SKILL_CATEGORIES)) {
        if (category.skills.includes(skillId)) {
            return category;
        }
    }
    return null;
}

// Gatherer configuration (pet food gathering zones)
export const GATHERER_CONFIG = {
    ZONES: {
        // Tier 1 - Beginner zones (levels 1-14)
        SHALLOW_GROVES: {
            id: 'shallow_groves',
            name: 'Shallow Groves',
            tier: 1,
            level: 1,
            duration: 3000, // 3 seconds
            xp: 15,
            description: 'Peaceful clearings with abundant basic resources.'
        },
        ROOTBOUND_FIELDS: {
            id: 'rootbound_fields',
            name: 'Rootbound Fields',
            tier: 1,
            level: 5,
            duration: 4000, // 4 seconds
            xp: 22,
            description: 'Overgrown meadows tangled with nutrient-rich roots.'
        },
        TANGLED_GROWTH: {
            id: 'tangled_growth',
            name: 'Tangled Growth',
            tier: 1,
            level: 10,
            duration: 6000, // 6 seconds
            xp: 35,
            description: 'Dense vegetation hiding valuable sustenance.'
        },
        // Tier 2 - Intermediate zones (levels 15-44)
        ICHOR_FLATS: {
            id: 'ichor_flats',
            name: 'Ichor Flats',
            tier: 2,
            level: 15,
            duration: 6000, // 6 seconds
            xp: 45,
            description: 'Marshlands infused with arcane residue.'
        },
        LIVING_THICKETS: {
            id: 'living_thickets',
            name: 'Living Thickets',
            tier: 2,
            level: 25,
            duration: 8000, // 8 seconds
            xp: 65,
            description: 'Semi-sentient vegetation with potent nutrients.'
        },
        WARPED_HOLLOWS: {
            id: 'warped_hollows',
            name: 'Warped Hollows',
            tier: 2,
            level: 35,
            duration: 10000, // 10 seconds
            xp: 90,
            description: 'Reality-bent groves with enhanced resources.'
        },
        // Tier 3 - Advanced zones (levels 45+)
        COREGROWTH_FIELDS: {
            id: 'coregrowth_fields',
            name: 'Coregrowth Fields',
            tier: 3,
            level: 45,
            duration: 10000, // 10 seconds
            xp: 120,
            description: 'Fields enriched by deep earth energies.'
        },
        BREACH_ZONES: {
            id: 'breach_zones',
            name: 'Breach Zones',
            tier: 3,
            level: 60,
            duration: 12000, // 12 seconds
            xp: 160,
            description: 'Rifts leaking concentrated planar essence.'
        },
        UNSTABLE_GROWTHS: {
            id: 'unstable_growths',
            name: 'Unstable Growths',
            tier: 3,
            level: 75,
            duration: 15000, // 15 seconds
            xp: 220,
            description: 'Volatile flora at the edge of reality.'
        }
    }
};

// Combat configuration
export const COMBAT_CONFIG = {
    MIN_HP: 10,                    // HP at combat level 1
    MAX_HP: 99,                    // HP cap at combat level 99 (virtual levels don't increase)
    RESPAWN_TIME: 25000,           // 25 second respawn timer
    MANUAL_EAT_COOLDOWN: 3000,     // 3 second cooldown for manual eating
    AUTO_EAT_COOLDOWN: 4500,       // 4.5 second cooldown for auto-eating
    AUTO_EAT_MIN_THRESHOLD: 10,    // Minimum threshold setting
    AUTO_EAT_MAX_THRESHOLD: 95,    // Maximum threshold setting
    AUTO_EAT_UNLOCK_LEVEL: 5,      // Combat level required to unlock auto-eat
    AUTO_EAT_ARCH_XP_BASE: 20,     // Base Architecting XP per auto-eat trigger (scales with Arch level)
    BASE_ATTACK_SPEED: 3000,       // Default attack speed in ms (3 seconds)
};

// Husbandry configuration (pet management)
/**
 * Husbandry configuration
 *
 * BALANCE REFERENCE (Loot Chest):
 * - ~20 unique items possible from all pet contracts
 * - Slots: 6 base → 18 max (via Pet Quarters milestones)
 * - Stack: 25 base → 200 max (via Pet Pantry milestones)
 * - Max capacity: 24 slots × 200 stack = 4,800 items
 */
export const HUSBANDRY_CONFIG = {
    MAX_PETS: 5,                    // Maximum active pets
    CHEST_BASE_SLOTS: 6,            // Base pet storage slots (upgradeable via Architecting)
    CHEST_MAX_SLOTS: 24,            // Maximum with all upgrades (6 + 12 from Pet Quarters + 6 from Beastmaster's Lodge)
    CHEST_CLAIM_ARCH_XP_BASE: 50,   // Base Architecting XP per loot chest claim (scales with Arch level)
    UNLOCK_LEVELS: {
        rootling: 1,                // Basic pet, unlocked from start
        felltracker: 15,            // Second tier pet
        ichorebound: 30,            // Third tier pet
        chimera: 50,                // Fourth tier pet
        eidolon: 75                 // Fifth tier pet
    }
};

// Pet configuration (evolution and contracts)
export const PET_CONFIG = {
    // Evolution costs per pet type (base + petIndex scaling)
    EVOLUTION_COSTS: {
        rootling: { base: 100, perIndex: 25 },
        felltracker: { base: 250, perIndex: 50 },
        ichorebound: { base: 500, perIndex: 100 },
        chimera: { base: 1000, perIndex: 200 },
        eidolon: { base: 2500, perIndex: 500 }
    },
    // Contract durations in milliseconds
    CONTRACT_DURATIONS: {
        short: 45 * 60 * 1000,      // 45 minutes
        medium: 90 * 60 * 1000,     // 90 minutes
        long: 2 * 60 * 60 * 1000    // 2 hours
    },
    // Research progress points per combat contract (by duration)
    // Longer contracts give proportionally more research
    RESEARCH_PROGRESS: {
        short: 1,                   // 45 min = 1 point
        medium: 3,                  // 90 min = 3 points (2x time, 3x reward)
        long: 5                     // 2 hours = 5 points (2.67x time, 5x reward)
    },
    // Evolution bonuses (drop multipliers) - reserved for talent tree
    EVOLUTION_BONUSES: {
        tier1: 1.0,                 // Base drops (drop bonuses come from talents)
        tier2: 1.0,                 // No drop bonus from evolution
        tier3: 1.0                  // No drop bonus from evolution
    }
};

// Item rarity for drop chances
export const RARITY = {
    COMMON: 1,
    UNCOMMON: 0.3,
    RARE: 0.1,
    VERY_RARE: 0.01,
};

export default {
    GAME_CONFIG,
    BANK_CONFIG,
    OFFLINE_CONFIG,
    FARM_CONFIG,
    BLOOMHOLD_CONFIG,
    COMBAT_CONFIG,
    XP_TABLE,
    SKILLS,
    SKILL_CATEGORIES,
    RARITY,
    GATHERER_CONFIG,
    HUSBANDRY_CONFIG,
    PET_CONFIG,
};